import React from 'react';

import {
  ArcElement, Chart as ChartJS, Legend, Title,
  Tooltip,
} from 'chart.js';
import { isEmpty } from 'lodash';
import { Pie } from 'react-chartjs-2';

import {
  ArrowUpwardIcon, AttachMoneyIcon, Box, CloudIcon,
  Container, LinearProgress, PersonAddIcon, Tooltip as TooltipMUI,
  Typography,
} from '../../components/shared/MaterialUI';
import { ChartStyles } from './charts-style';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const EMPTY_CHART_DATA = {
  labels: [],
  datasets: [{
    data: [0],
  }],
};

const EMPTY_MARKET_STATS = {
  weeklySignups: {
    label: 'Weekly signups',
    count: 0,
  },
  dailyRevenue: {
    label: 'Daily revenue',
    count: 0,
  },
  mowingCapacity: {
    label: 'Mowing capacity',
    capacity: 0,
    progress: 0,
    schedule: 0,
  },
};

const Charts = ({ market }) => {
  const classes = ChartStyles();

  const legendMargin = {
    id: 'legendMargin',
    beforeInit(chart) {
      const fitValue = chart.legend.fit;
      chart.legend.fit = function fit() {
        fitValue.bind(chart.legend)();
        return (this.width += 30);
      };
    },
  };

  const noDataToDisplay = {
    afterDraw: function (chart) {
      if (chart.data.datasets[0].data.every((item) => item === 0)) {
        let ctx = chart.ctx;
        let width = chart.width;
        let height = chart.height;

        chart.clear();
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('No data to display', width / 2, height / 2);
        ctx.restore(); // 14px \"Open Sans\"
        ctx.font = `14px "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`;
      }
    },
  };

  const plugins = [legendMargin, noDataToDisplay];

  const markets = market?.filter(Boolean) || [];

  return (
    <Container component='section' maxWidth='xl' disableGutters className={classes.chartsContainer}>
      {!isEmpty(markets) &&
        markets.map((marketItem, index) => {
          const chartData = marketItem?.dayNewSignups?.data;
          const data = chartData
            ? {
              labels: chartData.labels || [],
              datasets: [chartData.datasets || { data: [0] }],
            }
            : EMPTY_CHART_DATA;
          const options = marketItem?.dayNewSignups?.options || {};
          const weeklySignups = marketItem?.weeklySignups || EMPTY_MARKET_STATS.weeklySignups;
          const dailyRevenue = marketItem?.dailyRevenue || EMPTY_MARKET_STATS.dailyRevenue;
          const mowingCapacity = marketItem?.mowingCapacity || EMPTY_MARKET_STATS.mowingCapacity;
          const thereshold = Math.floor(mowingCapacity.capacity * 0.85);

          return (
            <Box key={index}>
              <Typography style={{ fontSize: '22px', fontWeight: 700 }}>{marketItem?.name || 'Unknown market'}</Typography>
              <Container
                component='article'
                key={index}
                maxWidth='xl'
                disableGutters
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '12px',
                  margin: '12px 0',
                  justifyContent: 'space-around',
                }}
              >
                <Box className={classes.chartBox}>
                  <Pie data={data} options={options} plugins={plugins} />
                </Box>
                <Box className={classes.metricsBox}>
                  <Box
                    className={classes.circleIcon}
                    style={{
                      backgroundColor: 'rgba(218, 230, 255, 1)',
                    }}
                    >
                      <PersonAddIcon style={{ fontSize: '24px', color: '#5887D6' }} />
                    </Box>
                  <Typography className='metricsTitle'>{weeklySignups.label}</Typography>
                  <Box className={classes.numberMetric}>
                    <ArrowUpwardIcon />
                    <Typography style={{ fontWeight: 700, fontFamily: 'Roboto', fontSize: '20px' }}>
                      {`${weeklySignups.count.toLocaleString()}`}
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.metricsBox}>
                  <Box
                    className={classes.circleIcon}
                    style={{
                      backgroundColor: '#FFF7DA',
                    }}
                    >
                      <AttachMoneyIcon style={{ fontSize: '24px', color: '#F2C94C' }} />
                    </Box>
                  <Typography className='metricsTitle'>{dailyRevenue.label}</Typography>
                  <Box className={classes.numberMetric}>
                    <ArrowUpwardIcon />
                    <Typography style={{ fontWeight: 700, fontFamily: 'Roboto', fontSize: '20px' }}>{`$${dailyRevenue.count.toLocaleString()}`}</Typography>
                  </Box>
                </Box>

                <Box className={classes.metricsBox}>
                  <Box
                    className={classes.circleIcon}
                    style={{
                      backgroundColor: '#DAFFF6',
                    }}
                    >
                      <CloudIcon style={{ fontSize: '24px', color: '#37EEC2' }} />
                    </Box>
                  <Typography className='metricsTitle'>{mowingCapacity.label}</Typography>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      style={{
                        fontWeight: 700,
                        fontFamily: 'Roboto',
                        fontSize: '20px',
                        lineHeight: '1',
                        textAlign: 'center',
                        marginBottom: '16px',
                      }}
                    >{`${mowingCapacity.capacity.toLocaleString()}`}</Typography>
                    <TooltipMUI
                      title={
                        <React.Fragment>
                          <Typography color='inherit'>
                            Mowing schedule: {mowingCapacity.schedule}
                          </Typography>
                        </React.Fragment>
                      }
                    >
                      <LinearProgress
                        variant='determinate'
                        value={mowingCapacity.progress >= mowingCapacity.capacity ? 100 : mowingCapacity.progress}
                        className={`${
                          mowingCapacity.schedule >= thereshold
                            ? classes.linearProgressDanger
                            : classes.linearProgress
                        }`}
                      />
                    </TooltipMUI>
                  </Box>
                </Box>
              </Container>
            </Box>
          );
        })}
    </Container>
  );
};

export default Charts;
